import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import { PermissionGate } from '@saas/ui';

/**
 * core の画面。apps/web が集約する。ルート単位で遅延ロード（03 第 I 部 §2）。
 * 管理画面は PermissionGate で core.* の権限を要求する（サーバ側の 403 とは別に、画面を出さない）。
 */
type PageModule = { default: ComponentType };

function page(load: () => Promise<PageModule>): RouteObject['lazy'] {
  return async () => {
    const m = await load();
    return { Component: m.default };
  };
}

function guarded(load: () => Promise<PageModule>, anyOf: string[]): RouteObject['lazy'] {
  return async () => {
    const m = await load();
    const Page = m.default;
    const Guarded = () => (
      <PermissionGate anyOf={anyOf}>
        <Page />
      </PermissionGate>
    );
    return { Component: Guarded };
  };
}

/** ログイン前（ログイン済みなら / へ） */
export const corePublicRoutes: RouteObject[] = [
  { path: '/login', lazy: page(() => import('./pages/LoginPage.js')) },
  { path: '/signup', lazy: page(() => import('./pages/SignupPage.js')) },
  { path: '/forgot-password', lazy: page(() => import('./pages/ForgotPasswordPage.js')) },
];

/** ログイン状態を問わない（招待・再設定のリンク。別のアカウントでログイン中でも開けて、ログアウトを促す） */
export const coreOpenRoutes: RouteObject[] = [
  { path: '/invite/:token', lazy: page(() => import('./pages/InviteAcceptPage.js')) },
  { path: '/reset-password/:token', lazy: page(() => import('./pages/ResetPasswordPage.js')) },
  // 段 5: Google / Microsoft ログインの戻り先（結果は URL のフラグメントで届く）
  { path: '/login/oidc', lazy: page(() => import('./pages/OidcCompletePage.js')) },
];

/** ログイン後（シェルの中） */
export const coreRoutes: RouteObject[] = [
  { path: '/', lazy: page(() => import('./pages/DashboardPage.js')) },
  { path: '/notifications', lazy: page(() => import('./pages/NotificationsPage.js')) },
  { path: '/settings/profile', lazy: page(() => import('./pages/ProfilePage.js')) },
  // 段 5: 2 要素認証・ログイン中の端末・外部ログイン（05 AUTH-05・04 §6.2）
  { path: '/settings/security', lazy: page(() => import('./pages/SecurityPage.js')) },
  { path: '/settings/notifications', lazy: page(() => import('./pages/NotificationSettingsPage.js')) },
  // SFA 1B: 自分のメールボックスの接続（SFA機能拡張設計書 §4・§6.1・§15.2.6）と、OAuth の戻り先
  // （${APP_URL}/settings/mail-connections/oauth/<provider>。画面が code / state を complete に渡す。calendar と同じ流儀）
  { path: '/settings/mail-connections', lazy: guarded(() => import('./pages/MailConnectionsPage.js'), ['core.mail.connect']) },
  { path: '/settings/mail-connections/oauth/:provider', lazy: guarded(() => import('./pages/MailOAuthCallbackPage.js'), ['core.mail.connect']) },
  { path: '/admin/users', lazy: guarded(() => import('./pages/admin/UsersPage.js'), ['core.user.read']) },
  { path: '/admin/api-keys', lazy: guarded(() => import('./pages/admin/ApiKeysPage.js'), ['core.tenant.manage']) },
  { path: '/admin/departments', lazy: guarded(() => import('./pages/admin/DepartmentsPage.js'), ['core.department.read']) },
  { path: '/admin/roles', lazy: guarded(() => import('./pages/admin/RolesPage.js'), ['core.role.read']) },
  { path: '/admin/tenant', lazy: guarded(() => import('./pages/admin/TenantPage.js'), ['core.tenant.read']) },
  // SFA 1A: 営業日カレンダー（SFA機能拡張設計書 §15.2.1）。変更は core.tenant.manage（画面の中で読み取り専用に切り替える）
  { path: '/admin/business-calendar', lazy: guarded(() => import('./pages/admin/BusinessCalendarPage.js'), ['core.tenant.read']) },
  { path: '/admin/identity', lazy: guarded(() => import('./pages/admin/IdentityPage.js'), ['core.tenant.manage']) },
  { path: '/admin/organization-changes', lazy: guarded(() => import('./pages/admin/OrganizationChangesPage.js'), ['core.department.manage']) },
  { path: '/admin/notifications', lazy: guarded(() => import('./pages/admin/NotificationTemplatesPage.js'), ['core.tenant.read']) },
  { path: '/admin/audit-logs', lazy: guarded(() => import('./pages/admin/AuditLogsPage.js'), ['core.audit.read']) },
  { path: '/admin/event-deliveries', lazy: guarded(() => import('./pages/admin/EventDeliveriesPage.js'), ['core.outbox.read']) },
  { path: '/admin/outbound-messages', lazy: guarded(() => import('./pages/admin/OutboundMessagesPage.js'), ['core.messaging.read']) },
  // gap-mail: メール接続の管理（会社全体の ON/OFF・社員の接続の状態と停止 / 再開 / 削除。件名・本文は出さない）
  { path: '/admin/mail-connections', lazy: guarded(() => import('./pages/admin/MailConnectionsAdminPage.js'), ['core.mail.connection.manage']) },
  { path: '/admin/usage', lazy: guarded(() => import('./pages/admin/UsagePage.js'), ['core.billing.read']) },
  // 段 5: 契約とプラン（02 §5.1・01 §4.1b）
  { path: '/admin/subscription', lazy: guarded(() => import('./pages/admin/SubscriptionPage.js'), ['core.billing.read']) },
  { path: '/admin/exports', lazy: guarded(() => import('./pages/admin/ExportsPage.js'), ['core.export.execute']) },
];
